/**
 * Plain-language names for request fields, shared by the Worker (validation
 * problem details) and the browser (error display).
 *
 * `fieldLabel("companyName")` → "Company name", `fieldLabel("targetUrl")` →
 * "Target URL", `fieldLabel("ownerEmployeeId")` → "Owner employee".
 */

export type ProblemFieldError = {
  /** The request path that failed, e.g. `companyName` or `rows.3.website`. */
  field: string;
  /** A human label for the field, e.g. `Company name`; empty for the whole request. */
  label: string;
  /** A complete, user-facing sentence. */
  message: string;
};

/** Paths that describe the whole request rather than one field. */
const REQUEST_FIELDS = new Set(["", "body", "request", "form", "_root"]);

const LABEL_OVERRIDES: Record<string, string> = {
  whatsappPhone: "WhatsApp number",
  gl: "Country",
  hl: "Language",
};

const WORD_OVERRIDES: Record<string, string> = {
  ai: "AI",
  api: "API",
  cta: "CTA",
  csv: "CSV",
  faq: "FAQ",
  ga4: "GA4",
  gsc: "GSC",
  html: "HTML",
  id: "ID",
  ids: "IDs",
  ip: "IP",
  json: "JSON",
  kpi: "KPI",
  linkedin: "LinkedIn",
  mfa: "MFA",
  otp: "OTP",
  pdf: "PDF",
  seo: "SEO",
  sla: "SLA",
  sms: "SMS",
  totp: "TOTP",
  uri: "URI",
  url: "URL",
  urls: "URLs",
  utm: "UTM",
  whatsapp: "WhatsApp",
};

export function isRequestField(field: string) {
  return REQUEST_FIELDS.has(field);
}

/** Turns one request key (camelCase, snake_case or kebab-case) into sentence case. */
export function fieldLabel(key: string): string {
  const override = LABEL_OVERRIDES[key];
  if (override) return override;
  const words = key
    .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/gu, "$1 $2")
    .split(/[\s_.-]+/u)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
  // "ownerEmployeeId" is a choice of employee, so "Owner employee" reads better than "... ID".
  if (words.length > 1 && (words.at(-1) === "id" || words.at(-1) === "ids")) words.pop();
  if (words.length === 0) return "";
  const phrase = words.map((word) => WORD_OVERRIDES[word] ?? word).join(" ");
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/**
 * Names the field at a validation path: the last named key, plus the row/item
 * number when the path goes through a list (`rows.3.website` → "Website (row 4)").
 */
export function fieldLabelFromPath(path: readonly PropertyKey[] | string | undefined): string {
  const segments =
    typeof path === "string" ? path.split(".").filter(Boolean) : [...(path ?? [])].map(String);
  if (segments.length === 1 && isRequestField(segments[0]!)) return "";
  let label = "";
  let position = "";
  for (const [index, segment] of segments.entries()) {
    if (/^\d+$/u.test(segment)) {
      const parent = segments[index - 1] ?? "";
      position = `${/rows?$/iu.test(parent) ? "row" : "item"} ${Number(segment) + 1}`;
    } else {
      label = fieldLabel(segment);
    }
  }
  if (!label) return position ? position.charAt(0).toUpperCase() + position.slice(1) : "";
  return position ? `${label} (${position})` : label;
}

/**
 * Flattens `{ field: [messages] }` into labelled entries, keeping any list
 * already provided by the server.
 */
export function problemFieldErrors(problem: {
  errors?: Record<string, string[]> | undefined;
  fieldErrors?: readonly Partial<ProblemFieldError>[] | undefined;
}): ProblemFieldError[] {
  if (problem.fieldErrors?.length) {
    return problem.fieldErrors
      .filter((entry): entry is Partial<ProblemFieldError> & { message: string } =>
        Boolean(entry && typeof entry.message === "string" && entry.message.trim()),
      )
      .map((entry) => {
        const field = typeof entry.field === "string" ? entry.field : "";
        return {
          field,
          label: typeof entry.label === "string" ? entry.label : fieldLabelFromPath(field),
          message: entry.message,
        };
      });
  }
  return Object.entries(problem.errors ?? {}).flatMap(([field, messages]) =>
    (Array.isArray(messages) ? messages : [])
      .filter((message) => typeof message === "string" && message.trim())
      .map((message) => ({ field, label: fieldLabelFromPath(field), message })),
  );
}

/**
 * One line for display: "Company name: This company is already in Leads".
 * The label is skipped when the message already names the field.
 */
export function describeFieldError({ label, message }: ProblemFieldError): string {
  const text = message.trim();
  if (!label) return text;
  const lowerText = text.toLowerCase();
  if (lowerText.includes(label.toLowerCase())) return text;
  const position = /\(((?:row|item) \d+)\)$/u.exec(label)?.[1];
  const bareLabel = position ? label.slice(0, label.lastIndexOf("(")).trim() : label;
  if (bareLabel && lowerText.includes(bareLabel.toLowerCase())) {
    return position ? `${position.charAt(0).toUpperCase()}${position.slice(1)}: ${text}` : text;
  }
  return `${label}: ${text}`;
}
